export type ScreenHitSample = {
  readonly clientX: number;
  readonly clientY: number;
  readonly surveySlug: string;
  readonly allowed: boolean;
};

/** Reuse the last hit result while the pointer barely moves (map units are expensive). */
export const SURVEY_DRAG_HIT_CACHE_PX = 8;

export function shouldReuseSurveyDragHit(
  previous: ScreenHitSample | null,
  clientX: number,
  clientY: number,
  surveySlug: string,
  cacheRadiusPx: number = SURVEY_DRAG_HIT_CACHE_PX,
): boolean {
  if (!previous || previous.surveySlug !== surveySlug) {
    return false;
  }
  return (
    Math.hypot(clientX - previous.clientX, clientY - previous.clientY) <
    cacheRadiusPx
  );
}

/**
 * Coalesce high-frequency dragover work onto one animation frame.
 * Returns a cancel function.
 */
export function createRafDragOverCoalescer(
  run: (event: DragEvent) => void,
): {
  readonly schedule: (event: DragEvent) => void;
  readonly cancel: () => void;
} {
  let rafId: number | null = null;
  let latest: DragEvent | null = null;

  const schedule = (event: DragEvent) => {
    latest = event;
    if (rafId != null) {
      return;
    }
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      const next = latest;
      latest = null;
      if (next) {
        run(next);
      }
    });
  };

  const cancel = () => {
    if (rafId != null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    latest = null;
  };

  return { schedule, cancel };
}

type ExtentLike = {
  readonly xmin?: number;
  readonly ymin?: number;
  readonly xmax?: number;
  readonly ymax?: number;
};

type GeometryWithExtent = {
  readonly extent?: ExtentLike | null;
};

/**
 * Cheap reject when the point sits far outside a geometry extent.
 * Padding is in the geometry's coordinate units (approx meters in Web Mercator).
 */
export function isMapPointOutsidePaddedExtent(
  mapPoint: { readonly x?: number; readonly y?: number },
  geometry: unknown,
  padding: number,
): boolean {
  if (typeof mapPoint.x !== 'number' || typeof mapPoint.y !== 'number') {
    return true;
  }
  const extent = (geometry as GeometryWithExtent | null | undefined)?.extent;
  if (
    !extent ||
    typeof extent.xmin !== 'number' ||
    typeof extent.ymin !== 'number' ||
    typeof extent.xmax !== 'number' ||
    typeof extent.ymax !== 'number'
  ) {
    return false;
  }
  const pad = Number.isFinite(padding) ? Math.max(0, padding) : 0;
  return (
    mapPoint.x < extent.xmin - pad ||
    mapPoint.x > extent.xmax + pad ||
    mapPoint.y < extent.ymin - pad ||
    mapPoint.y > extent.ymax + pad
  );
}
